/**
 * Edit Entry Dialog Component
 * Modal for editing or deleting an existing BOQ entry — Studio theme
 */

import { Loader2, Trash2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FieldInput, FloatPanel, StudioButton } from "@/features/studio/theme";
import {
	formatPricePaisa,
	paisaToRupeeInput,
	rupeeInputToPaisa,
} from "@/lib/format";
import {
	type BOQEntry,
	type BOQSection,
	deleteBOQEntry,
	updateBOQEntry,
} from "../../services/boq/boq-api.service";
import {
	getRoomSuggestions,
	type RoomReference,
} from "../../services/room-api.service";

interface EditEntryDialogProps {
	boqId: string;
	entry: BOQEntry | null;
	sections: BOQSection[];
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSuccess: () => void;
}

// ── Shared styled label ────────────────────────────────────────────────────────
function FieldLabel({
	children,
	htmlFor,
}: {
	children: React.ReactNode;
	htmlFor?: string;
}) {
	return (
		<label
			htmlFor={htmlFor}
			style={{
				display: "block",
				fontSize: 11,
				fontFamily: "var(--font-mono)",
				fontWeight: 600,
				letterSpacing: "0.09em",
				textTransform: "uppercase",
				color: "var(--color-dashboard-muted)",
				marginBottom: 6,
			}}
		>
			{children}
		</label>
	);
}

// ── Shared styled textarea ─────────────────────────────────────────────────────
function FieldTextarea({
	id,
	placeholder,
	value,
	onChange,
	rows = 2,
}: {
	id?: string;
	placeholder?: string;
	value: string;
	onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
	rows?: number;
}) {
	return (
		<textarea
			id={id}
			placeholder={placeholder}
			value={value}
			onChange={onChange}
			rows={rows}
			style={{
				width: "100%",
				padding: "10px 14px",
				fontSize: 14,
				fontFamily: "var(--font-body)",
				color: "var(--color-dashboard-text)",
				background: "var(--color-dashboard-paper)",
				border: "1px solid var(--color-dashboard-border)",
				borderRadius: 7,
				outline: "none",
				resize: "vertical",
				transition: "border-color 200ms, box-shadow 200ms",
				lineHeight: 1.5,
			}}
			onFocus={(e) => {
				e.currentTarget.style.borderColor = "var(--color-dashboard-accent)";
				e.currentTarget.style.boxShadow =
					"0 0 0 3px color-mix(in srgb, var(--color-dashboard-accent) 12%, transparent)";
			}}
			onBlur={(e) => {
				e.currentTarget.style.borderColor = "var(--color-dashboard-border)";
				e.currentTarget.style.boxShadow = "none";
			}}
		/>
	);
}

export function EditEntryDialog({
	boqId,
	entry,
	sections,
	open,
	onOpenChange,
	onSuccess,
}: EditEntryDialogProps) {
	const [description, setDescription] = useState("");
	const [quantity, setQuantity] = useState("1");
	const [unitPrice, setUnitPrice] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

	// Room state
	const [roomQuery, setRoomQuery] = useState("");
	const [roomSuggestions, setRoomSuggestions] = useState<RoomReference[]>([]);
	const [selectedRoom, setSelectedRoom] = useState<RoomReference | null>(null);
	const [customRoomName, setCustomRoomName] = useState("");
	const [isCustomRoom, setIsCustomRoom] = useState(false);
	const [showRoomSuggestions, setShowRoomSuggestions] = useState(false);
	const roomListRef = useRef<HTMLDivElement>(null);

	// Fetch room suggestions when query changes
	useEffect(() => {
		const fetchRooms = async () => {
			const rooms = await getRoomSuggestions(roomQuery);
			setRoomSuggestions(rooms);
		};
		fetchRooms();
	}, [roomQuery]);

	// Reset form when entry changes
	useEffect(() => {
		if (entry && open) {
			setDescription(entry.description || "");
			setQuantity(entry.quantity.toString());
			setUnitPrice(paisaToRupeeInput(entry.unitPrice));
			// Set room from entry
			if (entry.roomName) {
				setSelectedRoom({
					id: entry.roomReferenceId || "",
					name: entry.roomName,
					isDefault: true,
					createdBy: null,
				});
				setIsCustomRoom(false);
				setCustomRoomName("");
				setRoomQuery("");
			} else if (entry.roomCustomName) {
				setIsCustomRoom(true);
				setCustomRoomName(entry.roomCustomName);
				setRoomQuery(entry.roomCustomName);
				setSelectedRoom(null);
			} else {
				setSelectedRoom(null);
				setIsCustomRoom(false);
				setCustomRoomName("");
				setRoomQuery("");
			}
		}
	}, [entry, open]);

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				roomListRef.current &&
				!roomListRef.current.contains(event.target as Node)
			) {
				setShowRoomSuggestions(false);
			}
		};

		if (showRoomSuggestions) {
			document.addEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [showRoomSuggestions]);

	// Close on Escape
	useEffect(() => {
		if (!open) return;
		const handle = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				if (showDeleteConfirm) {
					setShowDeleteConfirm(false);
				} else {
					onOpenChange(false);
				}
			}
		};
		window.addEventListener("keydown", handle);
		return () => window.removeEventListener("keydown", handle);
	}, [open, onOpenChange, showDeleteConfirm]);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!entry || !quantity || !unitPrice) return;

		const trimmedQuantity = quantity.trim();
		if (!/^[1-9]\d*$/.test(trimmedQuantity)) {
			toast.error("Quantity must be a whole number");
			return;
		}
		const parsedQuantity = parseInt(trimmedQuantity, 10);

		setIsSubmitting(true);
		try {
			// Quantity and price are sent only when the designer changed them. A
			// homeowner's vendor-quote choice can reprice the line while this
			// dialog is open; sending back the price it opened with would undo
			// that, and restore the approval the reprice cleared.
			const parsedUnitPrice = rupeeInputToPaisa(unitPrice);
			// A cleared description is sent as "" so it is saved; an unchanged
			// one is left out.
			const trimmedDescription = description.trim();
			const changes = {
				description:
					trimmedDescription !== (entry.description ?? "")
						? trimmedDescription
						: undefined,
				quantity:
					parsedQuantity !== entry.quantity ? parsedQuantity : undefined,
				unitPrice:
					parsedUnitPrice !== entry.unitPrice ? parsedUnitPrice : undefined,
				// The room, likewise, only when it changed.
				roomReferenceId:
					!isCustomRoom &&
					selectedRoom?.id &&
					selectedRoom.id !== entry.roomReferenceId
						? selectedRoom.id
						: undefined,
				roomCustomName:
					isCustomRoom && customRoomName.trim() !== (entry.roomCustomName ?? "")
						? customRoomName.trim()
						: undefined,
			};
			// Nothing to send: the API refuses an empty update.
			if (Object.values(changes).every((value) => value === undefined)) {
				toast.info("No changes to save");
				onOpenChange(false);
				return;
			}
			const result = await updateBOQEntry(
				boqId,
				entry.boqSectionId,
				entry.id,
				changes,
			);

			if (result.success) {
				toast.success("Entry updated successfully");
				onSuccess();
				onOpenChange(false);
			} else {
				toast.error(result.error || "Failed to update entry");
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDelete = async () => {
		if (!entry) return;

		setIsDeleting(true);
		try {
			const result = await deleteBOQEntry(boqId, entry.boqSectionId, entry.id);

			if (result.success) {
				toast.success("Entry deleted successfully");
				setShowDeleteConfirm(false);
				onOpenChange(false);
				onSuccess();
			} else {
				toast.error(result.error || "Failed to delete entry");
			}
		} finally {
			setIsDeleting(false);
		}
	};

	// Find section name for display
	const sectionName =
		sections.find((s) => s.id === entry?.boqSectionId)?.sectionName ||
		"Unknown";

	if (!open || !entry) return null;

	return (
		<>
			{/* Edit Entry Modal */}
			<div
				role="dialog"
				aria-modal="true"
				style={{
					position: "fixed",
					inset: 0,
					zIndex: 200,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					padding: "20px 16px",
					background: "rgba(30, 41, 59, 0.45)",
					backdropFilter: "blur(4px)",
				}}
				onClick={(e) => {
					if (e.target === e.currentTarget) onOpenChange(false);
				}}
				onKeyDown={(e) => {
					if (e.key === "Escape") onOpenChange(false);
				}}
			>
				<FloatPanel
					elevation={4}
					padding={0}
					style={{ width: "100%", maxWidth: 500, overflow: "hidden" }}
				>
					{/* Header */}
					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							padding: "20px 24px 16px",
							borderBottom: "1px solid var(--color-dashboard-border)",
						}}
					>
						<div>
							<p
								style={{
									fontSize: 10,
									fontFamily: "var(--font-mono)",
									fontWeight: 600,
									letterSpacing: "0.14em",
									textTransform: "uppercase",
									color: "var(--color-dashboard-accent-dark)",
									marginBottom: 4,
								}}
							>
								BOQ
							</p>
							<h2
								style={{
									fontFamily: "var(--font-display)",
									fontStyle: "italic",
									fontSize: 20,
									fontWeight: 500,
									color: "var(--color-dashboard-text)",
									letterSpacing: "-0.01em",
								}}
							>
								Edit Entry
							</h2>
						</div>
						<button
							type="button"
							onClick={() => onOpenChange(false)}
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								width: 32,
								height: 32,
								borderRadius: 6,
								border: "1px solid var(--color-dashboard-border)",
								background: "transparent",
								cursor: "pointer",
								color: "var(--color-dashboard-muted)",
								transition: "background 150ms",
							}}
							onMouseEnter={(e) =>
								((e.currentTarget as HTMLElement).style.background =
									"var(--color-dashboard-divider)")
							}
							onMouseLeave={(e) =>
								((e.currentTarget as HTMLElement).style.background =
									"transparent")
							}
						>
							<X size={15} strokeWidth={2} />
						</button>
					</div>

					{/* Body */}
					<form onSubmit={handleSubmit}>
						<div
							style={{
								padding: "20px 24px",
								display: "flex",
								flexDirection: "column",
								gap: 16,
								maxHeight: "70vh",
								overflowY: "auto",
							}}
						>
							{entry.product && (
								<div
									style={{
										padding: "10px 14px",
										background: "var(--color-dashboard-divider)",
										borderRadius: 7,
										border: "1px solid var(--color-dashboard-border)",
									}}
								>
									<p
										style={{
											fontSize: 9,
											fontFamily: "var(--font-mono)",
											fontWeight: 600,
											letterSpacing: "0.08em",
											textTransform: "uppercase",
											color: "var(--color-dashboard-accent-dark)",
											marginBottom: 2,
										}}
									>
										Product Details
									</p>
									<p
										style={{
											fontSize: 13.5,
											fontWeight: 600,
											color: "var(--color-dashboard-text)",
											margin: 0,
										}}
									>
										{entry.product.name}
									</p>
									{entry.product.code && (
										<p
											style={{
												fontSize: 12,
												color: "var(--color-dashboard-muted)",
												margin: "2px 0 0 0",
											}}
										>
											Code: {entry.product.code}
										</p>
									)}
								</div>
							)}

							<div>
								<FieldLabel>Section</FieldLabel>
								<div
									style={{
										fontSize: 13.5,
										fontFamily: "var(--font-body)",
										color: "var(--color-dashboard-muted-dark)",
										background: "var(--color-dashboard-divider)",
										borderRadius: 7,
										padding: "10px 14px",
										border: "1px solid var(--color-dashboard-border)",
									}}
								>
									{sectionName}
								</div>
							</div>

							{/* Room selector */}
							<div style={{ position: "relative" }} ref={roomListRef}>
								<FieldLabel>Room</FieldLabel>
								<div
									style={{
										border: "1px solid var(--color-dashboard-border)",
										borderRadius: 6,
										overflow: "hidden",
									}}
								>
									<input
										placeholder="Select or type room name..."
										value={
											isCustomRoom
												? customRoomName
												: selectedRoom?.name || roomQuery
										}
										onChange={(e) => {
											const val = e.target.value;
											setIsCustomRoom(true);
											setCustomRoomName(val);
											setRoomQuery(val);
											setSelectedRoom(null);
											setShowRoomSuggestions(true);
										}}
										onFocus={() => setShowRoomSuggestions(true)}
										style={{
											width: "100%",
											padding: "10px 14px",
											fontSize: 14,
											border: "none",
											outline: "none",
											fontFamily: "var(--font-body)",
											background: "var(--color-dashboard-paper)",
										}}
									/>
									{showRoomSuggestions && roomSuggestions.length > 0 && (
										<div
											style={{
												position: "absolute",
												top: "100%",
												left: 0,
												right: 0,
												background: "var(--color-dashboard-paper)",
												border: "1px solid var(--color-dashboard-border)",
												borderRadius: 6,
												boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
												zIndex: 10,
												maxHeight: 200,
												overflowY: "auto",
											}}
										>
											{roomSuggestions.map((room) => (
												<button
													type="button"
													key={room.id}
													onClick={() => {
														setSelectedRoom(room);
														setIsCustomRoom(false);
														setCustomRoomName("");
														setRoomQuery("");
														setShowRoomSuggestions(false);
													}}
													style={{
														display: "block",
														width: "100%",
														padding: "10px 14px",
														textAlign: "left",
														border: "none",
														background: "transparent",
														cursor: "pointer",
														fontFamily: "var(--font-body)",
														fontSize: 14,
														color: "var(--color-dashboard-text)",
													}}
													onMouseEnter={(e) =>
														((e.currentTarget as HTMLElement).style.background =
															"var(--color-dashboard-divider)")
													}
													onMouseLeave={(e) =>
														((e.currentTarget as HTMLElement).style.background =
															"transparent")
													}
												>
													{room.name}
													{room.isDefault && (
														<span
															style={{
																marginLeft: 8,
																fontSize: 10,
																color: "var(--color-dashboard-muted)",
																textTransform: "uppercase",
															}}
														>
															Default
														</span>
													)}
												</button>
											))}
										</div>
									)}
								</div>
								{selectedRoom && !isCustomRoom && (
									<div
										style={{
											marginTop: 6,
											fontSize: 11,
											color: "var(--color-dashboard-muted)",
										}}
									>
										Selected: {selectedRoom.name}{" "}
										<button
											type="button"
											onClick={() => {
												setSelectedRoom(null);
												setIsCustomRoom(true);
											}}
											style={{
												background: "none",
												border: "none",
												cursor: "pointer",
												fontSize: 11,
												color: "var(--color-dashboard-accent-dark)",
												textDecoration: "underline",
											}}
										>
											Clear
										</button>
									</div>
								)}
							</div>

							<div
								style={{
									display: "grid",
									gridTemplateColumns:
										"repeat(auto-fit, minmax(min(100%, 160px), 1fr))",
									gap: 16,
								}}
							>
								<div>
									<FieldInput
										label="Quantity *"
										id="edit-quantity-input"
										type="number"
										min="1"
										step="1"
										value={quantity}
										onChange={(e) => setQuantity(e.target.value)}
										required
									/>
								</div>
								<div>
									<FieldInput
										label="Unit Price (₹) *"
										id="edit-unit-price-input"
										type="number"
										min="0"
										step="0.01"
										value={unitPrice}
										onChange={(e) => setUnitPrice(e.target.value)}
										required
									/>
									<p
										style={{
											fontSize: 10,
											color: "var(--color-dashboard-muted)",
											marginTop: 4,
											fontWeight: 500,
										}}
									>
										= {formatPricePaisa(rupeeInputToPaisa(unitPrice || "0"))}{" "}
										per unit
									</p>
								</div>
							</div>

							<div>
								<FieldLabel htmlFor="edit-entry-desc-input">
									Site Notes (optional)
								</FieldLabel>
								<FieldTextarea
									id="edit-entry-desc-input"
									placeholder="e.g. For master bedroom only, verified with client"
									value={description}
									onChange={(e) => setDescription(e.target.value)}
									rows={2}
								/>
							</div>

							{/* Delete button */}
							<button
								type="button"
								onClick={() => setShowDeleteConfirm(true)}
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									gap: 8,
									width: "100%",
									height: 40,
									borderRadius: 7,
									border:
										"1px solid var(--color-dashboard-danger-border-strong)",
									background: "var(--color-dashboard-danger-bg)",
									color: "var(--color-dashboard-pill-risk)",
									fontFamily: "var(--font-body)",
									fontSize: 13.5,
									fontWeight: 500,
									cursor: "pointer",
									transition: "background 150ms",
									marginTop: 4,
								}}
								onMouseEnter={(e) =>
									((e.currentTarget as HTMLElement).style.background =
										"var(--color-dashboard-pill-risk-bg)")
								}
								onMouseLeave={(e) =>
									((e.currentTarget as HTMLElement).style.background =
										"var(--color-dashboard-danger-bg)")
								}
							>
								<Trash2 size={15} />
								Delete Entry
							</button>
						</div>

						{/* Footer */}
						<div
							style={{
								display: "flex",
								justifyContent: "flex-end",
								gap: 10,
								padding: "16px 24px",
								borderTop: "1px solid var(--color-dashboard-border)",
								background: "var(--color-dashboard-surface)",
							}}
						>
							<StudioButton
								variant="secondary"
								size="sm"
								type="button"
								onClick={() => onOpenChange(false)}
							>
								Cancel
							</StudioButton>
							<StudioButton
								variant="primary"
								size="sm"
								type="submit"
								disabled={isSubmitting || !quantity || !unitPrice}
							>
								{isSubmitting ? "Saving…" : "Save Changes"}
							</StudioButton>
						</div>
					</form>
				</FloatPanel>
			</div>

			{/* Delete Confirmation Modal */}
			{showDeleteConfirm && (
				<div
					role="dialog"
					aria-modal="true"
					style={{
						position: "fixed",
						inset: 0,
						zIndex: 210,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						padding: "20px 16px",
						background: "rgba(30, 41, 59, 0.45)",
						backdropFilter: "blur(4px)",
					}}
					onClick={(e) => {
						if (e.target === e.currentTarget) setShowDeleteConfirm(false);
					}}
					onKeyDown={(e) => {
						if (e.key === "Escape") setShowDeleteConfirm(false);
					}}
				>
					<FloatPanel
						elevation={4}
						padding={0}
						style={{ width: "100%", maxWidth: 420, overflow: "hidden" }}
					>
						<div style={{ padding: "24px 24px 20px" }}>
							<h3
								style={{
									fontFamily: "var(--font-display)",
									fontStyle: "italic",
									fontSize: 18,
									fontWeight: 500,
									color: "var(--color-dashboard-text)",
									margin: "0 0 10px 0",
								}}
							>
								Delete Entry
							</h3>
							<p
								style={{
									fontFamily: "var(--font-body)",
									fontSize: 14,
									color: "var(--color-dashboard-muted-dark)",
									lineHeight: 1.5,
									margin: 0,
								}}
							>
								Are you sure you want to delete this entry?
								{entry.product && (
									<span
										style={{
											display: "block",
											marginTop: 8,
											fontWeight: 600,
											color: "var(--color-dashboard-text)",
										}}
									>
										Product: {entry.product.name}
									</span>
								)}
								This action cannot be undone.
							</p>
						</div>

						<div
							style={{
								display: "flex",
								justifyContent: "flex-end",
								gap: 10,
								padding: "16px 24px",
								borderTop: "1px solid var(--color-dashboard-border)",
								background: "var(--color-dashboard-surface)",
							}}
						>
							<StudioButton
								variant="secondary"
								size="sm"
								type="button"
								onClick={() => setShowDeleteConfirm(false)}
								disabled={isDeleting}
							>
								Cancel
							</StudioButton>
							<button
								type="button"
								onClick={handleDelete}
								disabled={isDeleting}
								style={{
									display: "inline-flex",
									alignItems: "center",
									justifyContent: "center",
									height: 36,
									paddingLeft: 14,
									paddingRight: 14,
									fontSize: 13,
									fontFamily: "var(--font-body)",
									fontWeight: 500,
									borderRadius: 8,
									border: "none",
									background: "var(--color-dashboard-danger-fill)",
									color: "var(--color-dashboard-on-fill)",
									cursor: isDeleting ? "not-allowed" : "pointer",
									opacity: isDeleting ? 0.6 : 1,
									transition: "background 150ms",
								}}
								onMouseEnter={(e) => {
									if (!isDeleting)
										e.currentTarget.style.background =
											"var(--color-dashboard-danger)";
								}}
								onMouseLeave={(e) => {
									if (!isDeleting)
										e.currentTarget.style.background =
											"var(--color-dashboard-pill-risk)";
								}}
							>
								{isDeleting ? (
									<>
										<Loader2
											size={14}
											style={{
												marginRight: 6,
												animation: "spin 1s linear infinite",
											}}
										/>
										Deleting…
									</>
								) : (
									"Delete Entry"
								)}
							</button>
						</div>
					</FloatPanel>
				</div>
			)}
		</>
	);
}
